import { HangoutState } from '../state/HangoutState.js';
import { fetchCharacterSprite } from './character-manager.js';
import { storage } from '../../../js/utils/storage.js';
import { conversationQueue } from './conversation-queue.js';
import { $, $$ , removeClass } from '../../../core/utils/dom.js';
import { getDisplayManager } from './hangout-display-manager.js';
import { getCurrentLayoutMode, isVnLayoutMode, LAYOUT_MODES } from './layout-mode.js';
import { getVoice } from './voice-utils.js';
import { persistChatSummaryFromLocal } from './hangout-settings-persist.js';
import { setChatMemory } from './hangout-memory-storage.js';
import { isResolvedConversationId } from './request-builder.js';
import { getCurrentLanguage } from '../../translations.js';
import { showToast } from '../../utils/toast.js';
import { showConfirm } from '../../utils/UnifiedNotificationSystem.js';
import { getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { openMessageImagePromptModal } from './message-image-prompt-modal.js';
import { openMessageSpriteReferenceModal } from './message-sprite-reference-modal.js';
import { resolveLatestCharacterSpriteData, hasValidResponseActionId, hasValidResponseOutfitId, hasValidResponseLocation } from '../shared/sprite-state.js';
import { applyHangoutLocationTransition } from './location-transition.js';
import { getSettingsModalTranslationText } from './modals/settings-dom-utils.js';
import { sanitizeCrossCharacterBleed } from '../shared/character-bleed.js';
import { trimUnfinishedSentence } from '../shared/trim-unfinished-sentence.js';
import { resolveTextPlaceholders, resolveUserNameFromStorage } from '../../../js/utils/placeholder-resolver.js';
import { markCharacterSpeaking, isMobilePortrait, activateConversationMode, isSingleCharacterContainerMode } from './mobile-handlers.js';
import { getBackgroundServiceInstance, getCurrentBackgroundSafely } from './hangout-background-access.js';
import { getTranscriptForLayout } from '../shared/history-resolution.js';
import {
    getHangoutConfig,
    getCharacterDisplayIndex,
    getCurrentOutfitForCharacter,
    updateHangoutConfigAfterMessage,
    patchCharacterInHangoutConfig,
    setHangoutConfig
} from '../shared/hangout-config-utils.js';
import {
    refreshVnTurnNavigationFromLatestRound,
    resolvePlayableIsVideo,
    resolveSceneFieldsForMessage
} from './hangout-scene.js';
import {
    normalizeMessagePath,
    resolveCanonicalPath,
    isDescendantPath,
    resolveGreetingChainPath
} from '../shared/message-tree.js';
import { applyEnergyFromServer } from '../../../components/navigation/energy.js';

let activeStreamSession = null;

export const beginStreamSession = ({ userPath = null } = {}) => {
    activeStreamSession = {
        id: `stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        userPath: normalizeMessagePath(userPath) || null
    };
    return activeStreamSession;
};

export const getActiveStreamSession = () => activeStreamSession;

export const endStreamSession = (sessionId = null) => {
    if (!activeStreamSession) return;
    if (sessionId && activeStreamSession.id !== sessionId) return;
    activeStreamSession = null;
};

const isIncomingPathOnViewedBranch = (path) =>
    HangoutState.getInstance().shouldAdoptIncomingPath(path);

const resolveStreamFindOptions = (options = {}) => {
    const streamGenerationId = options.streamGenerationId || getActiveStreamSession()?.id || null;
    return streamGenerationId ? { streamGenerationId } : {};
};

const syncCrossLayoutStreamText = async (responseData, displayText, options, bubble) => {
    try {
        const displayManager = await getDisplayManager();
        const activeMessage = displayManager.findMessageByCharacterId(
            responseData.characterId,
            resolveStreamFindOptions(options)
        );
        if (activeMessage && activeMessage !== bubble) {
            displayManager.updateMessageText(responseData.characterId, displayText, resolveStreamFindOptions(options));
        }
    } catch {
        // Ignore cross-layout sync issues during streaming.
    }
};

const activateSpeakingUi = async (characterId, characterIndex, characters) => {
    if (!isMobilePortrait() || !isVnLayoutMode() || isSingleCharacterContainerMode()) return;
    markCharacterSpeaking(characterIndex, characterId);
    const displayManager = await getDisplayManager();
    await activateConversationMode(characters, displayManager);
};

const applySpeakingSprite = async (responseData, characterIndex, isVideo, outfitId) => {
    const { saveAllZoomStates } = await import('./imageZoom.js');
    saveAllZoomStates();
    await fetchCharacterSprite(
        responseData.characterId,
        responseData.actionId,
        outfitId,
        Math.max(0, characterIndex),
        true,
        isVideo
    );
};

const handleLocationEvent = async (responseData, { isInitialGreeting = false, requireValidLocation = false } = {}) => {
    const locationId = isInitialGreeting
        ? responseData.locationId
        : (responseData.location || responseData.locationId);
    if (requireValidLocation && !hasValidResponseLocation(responseData.location ?? responseData.locationId)) {
        return;
    }
    await applyHangoutLocationTransition({
        locationId,
        timeOfDay: responseData.timeOfDay,
        backgroundUrl: responseData.backgroundUrl,
        isInitialGreeting: isInitialGreeting || responseData.isInitialGreeting,
        skipIfMatchesConfig: true,
        getBackgroundServiceInstance,
        getCurrentBackgroundSafely
    });
};

const getCurrentActionForCharacter = async (characterId) => {
    const hangoutConfig = await getHangoutConfig();
    const { actionId } = resolveLatestCharacterSpriteData({
        messages: HangoutState.getInstance().getMessages() || [],
        characterId,
        config: hangoutConfig
    });
    return hasValidResponseActionId(actionId) ? actionId : null;
};

const persistAssistantResponse = async ({
    responseData,
    finalText,
    finalActionId,
    finalOutfitId,
    isVideoMsg,
    sceneFields,
    isGreetingResponse,
    shouldAdoptView,
    streamFindOptions,
    activeCharacters
}) => {
    const hangoutState = HangoutState.getInstance();
    const addResult = await hangoutState.addMessage({
        role: 'assistant',
        content: finalText,
        actionId: finalActionId,
        actionName: responseData.actionName,
        emotionId: responseData.emotionId,
        characterId: responseData.characterId,
        characterName: responseData.characterName,
        outfitId: finalOutfitId,
        isVideo: isVideoMsg,
        ...sceneFields,
        ...(isGreetingResponse
            ? {
                path: resolveGreetingChainPath(
                    responseData.characterId,
                    activeCharacters,
                    HangoutState.getInstance().getGreetingVariationMeta().variation
                ),
                isGreeting: true,
                isInitialGreeting: true
            }
            : (responseData.path ? { path: String(responseData.path) } : {}))
    }, false);

    if (!isGreetingResponse && shouldAdoptView && responseData.path) {
        const canonicalPath = responseData.canonicalPath
            || resolveCanonicalPath(responseData.path, hangoutState.getMessages());
        hangoutState.ensureActiveVersion(canonicalPath, responseData.path);
    }

    const boundPath = normalizeMessagePath(responseData.path || addResult?.messagePath);
    if (shouldAdoptView && boundPath && getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
        const displayManager = await getDisplayManager();
        displayManager.bindPathToLatestAssistantMessage?.(responseData.characterId, boundPath, {
            canonicalPath: responseData.canonicalPath || null,
            ...streamFindOptions
        });
    }

    if (isVnLayoutMode()) {
        await refreshVnTurnNavigationFromLatestRound(responseData.characterId, {
            preserveUserNavigation: true
        });
    }

    return addResult;
};

const REASONING_TOKENS_EXHAUSTED_FALLBACK =
    'All tokens were consumed in the reasoning step. If this keeps happening, please consider reducing reasoning or increasing the max_tokens value.';

const applyReasoningPreview = (bubble, enabled) => {
    if (!bubble) return;
    bubble.isReasoningPreview = enabled === true;
    bubble.setReasoningPreview?.(enabled === true);
};

const showReasoningTokensExhaustedToast = () => {
    const lang = getCurrentLanguage();
    const message = window.translations?.[lang]?.hangout?.notifications?.reasoning_tokens_exhausted
        ?? window.translations?.en?.hangout?.notifications?.reasoning_tokens_exhausted
        ?? REASONING_TOKENS_EXHAUSTED_FALLBACK;
    showToast(message, 'warning', { duration: 8000 });
};

const handleResponseStart = async (responseData, characters, characterResponses, options = {}) => {
    const isManualPacing = isVnLayoutMode() && storage.get('manualCharacterPacing') !== 'false';
    const isFirstResponse = isManualPacing && !window._hasStreamedFirstResponse;
    const isWelcomeBackResponse = window._welcomeBackInProgress;

    if (isFirstResponse) window._hasStreamedFirstResponse = true;
    if (isWelcomeBackResponse) window._welcomeBackInProgress = false;

    const startCharacter = characters.find((c) => String(c.id) === String(responseData.characterId));
    const startOutfitId = responseData.outfitId ?? startCharacter?.outfitId ?? 0;
    const isVideo = await resolvePlayableIsVideo(responseData.characterId, startOutfitId, responseData.actionId);

    if (getCurrentLayoutMode() === LAYOUT_MODES.CHATBOT) {
        const hangoutContainer = $('#hangout-container');
        if (hangoutContainer) removeClass(hangoutContainer, 'conversation-active');
        $$('.currently-speaking').forEach((el) => removeClass(el, 'currently-speaking'));
    }

    if (responseData.characterId) {
        const displayManager = await getDisplayManager();
        const streamFindOptions = resolveStreamFindOptions(options);
        if (isVnLayoutMode()) {
            await displayManager.removeTypingIndicator(responseData.characterId);
            if (displayManager.isAvailable()) {
                const existingMessage = displayManager.findMessageByCharacterId(
                    responseData.characterId,
                    streamFindOptions
                );
                if (existingMessage && existingMessage.isTyping) {
                    existingMessage.isTyping = false;
                    existingMessage.isAwaitingDecision = true;
                    displayManager.updateMessageText(responseData.characterId, '');
                    existingMessage.voiceButtonState = 'play';
                    existingMessage.isAudioPlaying = false;
                    existingMessage.currentVoiceKey = null;
                }
            }
        }
    }

    if (hasValidResponseActionId(responseData.actionId)) {
        const character = characters.find((c) => String(c.id) === String(responseData.characterId));
        if (character) {
            character.actionId = responseData.actionId;
            if (responseData.actionName) character.actionName = responseData.actionName;
            character.isVideo = isVideo;

            await patchCharacterInHangoutConfig(responseData.characterId, {
                actionId: responseData.actionId,
                ...(responseData.actionName ? { actionName: responseData.actionName } : {}),
                isVideo
            });

            const streamUserPath = options.streamUserPath || getActiveStreamSession()?.userPath || null;
            const shouldApplyStageSprite = !streamUserPath || isIncomingPathOnViewedBranch(streamUserPath);
            if (shouldApplyStageSprite && (!isManualPacing || isFirstResponse)) {
                const displayManager = await getDisplayManager();
                if (displayManager.isAvailable()) {
                    const messageToUpdate = displayManager.findMessageByCharacterId(
                        responseData.characterId,
                        resolveStreamFindOptions(options)
                    );
                    if (!messageToUpdate) {
                        const currentMessages = displayManager.getActiveMessages();
                        displayManager.setActiveMessages(currentMessages.filter((m) =>
                            String(m.characterId) !== String(responseData.characterId)
                        ));
                        displayManager.redrawAllMessages();
                    }
                }

                const characterIndex = await getCharacterDisplayIndex(responseData.characterId);
                try {
                    await activateSpeakingUi(responseData.characterId, characterIndex, characters);
                    await applySpeakingSprite(responseData, characterIndex, isVideo, responseData.outfitId || character.outfitId || 0);
                } catch (error) {
                    console.error('message-stream: Error starting speaking animation:', error);
                }
            }
        }
    }

    if (!(isFirstResponse || isWelcomeBackResponse) && isManualPacing) {
        const queuedResponse = {
            characterId: responseData.characterId,
            characterName: responseData.characterName,
            rawContent: '',
            content: '',
            reasoning: '',
            isReasoningPreview: false,
            reasoningExhausted: false,
            bubble: null,
            isQueued: true,
            responseData,
            streamingChunks: []
        };
        characterResponses.set(responseData.characterId, queuedResponse);
        return queuedResponse;
    }

    const displayManager = await getDisplayManager();
    const spriteStateForBubble = {
        characterId: responseData.characterId,
        actionId: responseData.actionId ?? null,
        actionName: responseData.actionName || null,
        outfitId: responseData.outfitId ?? 0,
        isVideo
    };
    const bubble = await displayManager.createMessage(
        options.isContinue ? (options.continuePrefix || '') : '...',
        responseData.characterName,
        responseData.characterId,
        spriteStateForBubble,
        {
            ...resolveStreamFindOptions(options),
            isGreeting: options.isInitialGreeting === true
        }
    );
    if (bubble) {
        bubble.isTyping = true;
        bubble.isAwaitingDecision = true;
        if (options.isContinue) applyReasoningPreview(bubble, false);
        bubble.markDirty?.();
    }
    if (!window.__voicePrefetchDone && storage.get('autoVoiceEnabled') !== 'false') {
        window.__voicePrefetchDone = true;
        getVoice().catch(() => {});
    }

    characterResponses.set(responseData.characterId, {
        characterId: responseData.characterId,
        characterName: responseData.characterName,
        rawContent: '',
        content: '',
        reasoning: '',
        isReasoningPreview: false,
        reasoningExhausted: false,
        bubble,
        isQueued: false
    });
};

const getStreamDisplayProgress = (previousDisplayText, nextDisplayText) => {
    if (typeof nextDisplayText !== 'string' || nextDisplayText.length === 0) {
        return { appendedText: '', requiresReset: false };
    }
    if (typeof previousDisplayText !== 'string' || previousDisplayText.length === 0) {
        return { appendedText: nextDisplayText, requiresReset: false };
    }
    if (nextDisplayText.startsWith(previousDisplayText)) {
        return {
            appendedText: nextDisplayText.slice(previousDisplayText.length),
            requiresReset: false
        };
    }
    return { appendedText: nextDisplayText, requiresReset: true };
};

const applyStreamBubbleText = (charResponse, responseData, displayText, options) => {
    if (charResponse.bubble) charResponse.bubble.setText(displayText);
    void syncCrossLayoutStreamText(responseData, displayText, options, charResponse.bubble);
    const chatHistory = $('#chat-history');
    if (chatHistory) chatHistory.scrollTop = chatHistory.scrollHeight;
};

const handleReasoningChunk = async (responseData, characterResponses, options = {}) => {
    const charResponse = characterResponses.get(responseData.characterId);
    if (!charResponse) return;

    const rawChunk = typeof responseData.content === 'string' ? responseData.content : '';
    if (!rawChunk) return charResponse;

    charResponse.reasoning = `${charResponse.reasoning || ''}${rawChunk}`;
    charResponse.isReasoningPreview = true;
    applyReasoningPreview(charResponse.bubble, true);

    if (charResponse.isQueued) {
        charResponse.streamingChunks = charResponse.streamingChunks || [];
        return charResponse;
    }

    applyStreamBubbleText(charResponse, responseData, charResponse.reasoning, options);
    return charResponse;
};

const handleContentChunk = async (responseData, characterResponses, options = {}) => {
    const { displayUsername = 'User', continuePrefix = '', activeCharacters = [] } = options;
    const charResponse = characterResponses.get(responseData.characterId);
    if (!charResponse) return;

    const rawChunk = typeof responseData.content === 'string' ? responseData.content : '';
    if (!rawChunk) return charResponse;

    if (charResponse.isReasoningPreview) {
        charResponse.isReasoningPreview = false;
        charResponse.reasoning = '';
        charResponse.rawContent = '';
        charResponse.content = '';
        applyReasoningPreview(charResponse.bubble, false);
    }

    const previousRawContent = typeof charResponse.rawContent === 'string' ? charResponse.rawContent : '';
    const previousDisplayText = typeof charResponse.content === 'string' ? charResponse.content : '';
    const nextRawContent = responseData.replace === true ? rawChunk : `${previousRawContent}${rawChunk}`;
    const nextDisplayText = resolveTextPlaceholders(nextRawContent, {
        userName: displayUsername,
        scenarioChars: activeCharacters,
        fallbackCharName: 'Character'
    });
    if (nextDisplayText === previousDisplayText) {
        charResponse.rawContent = nextRawContent;
        return charResponse;
    }

    const { appendedText, requiresReset } = getStreamDisplayProgress(previousDisplayText, nextDisplayText);
    charResponse.rawContent = nextRawContent;
    charResponse.content = nextDisplayText;
    const displayText = continuePrefix ? `${continuePrefix}${nextDisplayText}` : nextDisplayText;

    if (charResponse.isQueued) {
        if (requiresReset) charResponse.streamingChunks = [nextDisplayText];
        else if (appendedText) charResponse.streamingChunks.push(appendedText);
        return charResponse;
    }

    applyStreamBubbleText(charResponse, responseData, displayText, options);
    return charResponse;
};

const handleTextComplete = async (responseData, characterResponses, options = {}) => {
    const { activeCharacters = [], displayUsername = 'User' } = options;
    const charResponse = characterResponses.get(responseData.characterId);
    if (!charResponse) return;

    let finalText = sanitizeCrossCharacterBleed(
        charResponse.content || '',
        responseData.characterId,
        activeCharacters
    );
    finalText = resolveTextPlaceholders(finalText, {
        userName: displayUsername,
        scenarioChars: activeCharacters,
        fallbackCharName: 'Character'
    });
    const keepReasoningPreview = (responseData.reasoningExhausted === true || !finalText.trim())
        && Boolean(charResponse.reasoning);
    if (responseData.outputTruncated === true && !keepReasoningPreview) {
        const trimmedText = trimUnfinishedSentence(finalText);
        if (trimmedText !== finalText) {
            finalText = trimmedText;
            if (charResponse.isQueued) charResponse.streamingChunks = [finalText];
        }
    }
    charResponse.content = finalText;
    charResponse.textComplete = true;
    if (keepReasoningPreview) {
        charResponse.reasoningExhausted = true;
        charResponse.isReasoningPreview = true;
        applyReasoningPreview(charResponse.bubble, true);
    }
    const displayText = keepReasoningPreview
        ? charResponse.reasoning
        : (options.continuePrefix ? `${options.continuePrefix}${finalText}` : finalText);

    if (charResponse.bubble) {
        if (!keepReasoningPreview) charResponse.bubble.setText(displayText);
        charResponse.bubble.isTyping = false;
        if (charResponse.bubble.isAwaitingDecision !== false) {
            charResponse.bubble.isAwaitingDecision = true;
        }
        charResponse.bubble.markDirty?.();
    }

    try {
        const displayManager = await getDisplayManager();
        const activeMessage = displayManager.findMessageByCharacterId(
            responseData.characterId,
            resolveStreamFindOptions(options)
        );
        if (activeMessage) {
            activeMessage.isTyping = false;
            if (activeMessage.isAwaitingDecision !== false) {
                activeMessage.isAwaitingDecision = true;
            }
            if (keepReasoningPreview) {
                applyReasoningPreview(activeMessage, true);
            } else if (activeMessage !== charResponse.bubble) {
                applyReasoningPreview(activeMessage, false);
                displayManager.updateMessageText(responseData.characterId, displayText, resolveStreamFindOptions(options));
            }
            if (typeof activeMessage.markDirty === 'function') {
                activeMessage.markDirty();
            } else {
                activeMessage._renderVersionControls?.();
            }
        }
    } catch {
        // Ignore cross-layout sync issues when text completes early.
    }
};

const handleOutfitChange = async (responseData) => {
    try {
        const { getLocationTransitionManager } = await import('./location-transition.js');
        getLocationTransitionManager().setPendingOutfitChange(responseData.characterId, responseData.outfitId);
    } catch (error) {
        console.error('message-stream: Error storing pending outfit change:', error);
        await patchCharacterInHangoutConfig(responseData.characterId, { outfitId: responseData.outfitId });
    }
};

const handleResponseEnd = async (responseData, characterResponses, options = {}) => {
    const { isRegularMessage = true, activeCharacters = [], displayUsername = 'User' } = options;
    const charResponse = characterResponses.get(responseData.characterId);
    let finalText = '';

    if (charResponse) {
        finalText = sanitizeCrossCharacterBleed(
            charResponse.content || '',
            responseData.characterId,
            activeCharacters
        );
        finalText = resolveTextPlaceholders(finalText, {
            userName: displayUsername,
            scenarioChars: activeCharacters,
            fallbackCharName: 'Character'
        });
        if (charResponse.bubble) {
            charResponse.bubble.isTyping = false;
            charResponse.bubble.isAwaitingDecision = false;
            charResponse.bubble.markDirty?.();
        }
    }

    const keepReasoningPreview = (responseData.reasoningExhausted === true || !String(finalText || '').trim())
        && Boolean(charResponse?.reasoning);
    if (keepReasoningPreview) {
        charResponse.reasoningExhausted = true;
        charResponse.isReasoningPreview = true;
        applyReasoningPreview(charResponse.bubble, true);
    }

    try {
        const displayManager = await getDisplayManager();
        const activeMessage = displayManager.findMessageByCharacterId(
            responseData.characterId,
            resolveStreamFindOptions(options)
        );
        if (activeMessage) {
            activeMessage.isTyping = false;
            activeMessage.isAwaitingDecision = false;
            if (typeof activeMessage.markDirty === 'function') {
                activeMessage.markDirty();
            } else {
                activeMessage._renderVersionControls?.();
            }
        }
    } catch {
        // Ignore display sync issues when clearing decision gate.
    }

    // Sentinel actionId/outfitId/location keep the current session values.
    const hasNewOutfit = hasValidResponseOutfitId(responseData.outfitId);
    const hasNewAction = hasValidResponseActionId(responseData.actionId);
    const hasNewLocation = responseData.isLocationTransition === true
        && hasValidResponseLocation(responseData.location ?? responseData.locationId);
    const finalOutfitId = hasNewOutfit
        ? responseData.outfitId
        : await getCurrentOutfitForCharacter(responseData.characterId);
    const finalActionId = hasNewAction
        ? responseData.actionId
        : await getCurrentActionForCharacter(responseData.characterId);
    const isVideoMsg = await resolvePlayableIsVideo(
        responseData.characterId,
        finalOutfitId,
        finalActionId
    );

    const incomingPath = normalizeMessagePath(responseData.path)
        || getActiveStreamSession()?.userPath
        || null;
    const shouldAdoptView = !incomingPath || isIncomingPathOnViewedBranch(incomingPath);
    const streamFindOptions = resolveStreamFindOptions(options);

    if (options.isContinue && options.continuePath) {
        const prefix = String(options.continuePrefix || '');
        const combinedText = finalText && finalText.startsWith(prefix)
            ? finalText
            : `${prefix}${finalText || ''}`;
        const hangoutState = HangoutState.getInstance();
        if (!keepReasoningPreview) {
            await hangoutState.updateMessageContent(options.continuePath, combinedText, { persistRemote: false });
        }
        try {
            const displayManager = await getDisplayManager();
            const activeMessage = displayManager.findMessageByCharacterId(
                responseData.characterId,
                streamFindOptions
            ) || displayManager.bindPathToLatestAssistantMessage?.(
                responseData.characterId,
                options.continuePath,
                streamFindOptions
            );
            if (activeMessage) {
                activeMessage.isTyping = false;
                activeMessage.isAwaitingDecision = false;
                if (keepReasoningPreview) {
                    applyReasoningPreview(activeMessage, true);
                } else {
                    applyReasoningPreview(activeMessage, false);
                    displayManager.updateMessageText(responseData.characterId, combinedText, streamFindOptions);
                }
                activeMessage._renderVersionControls?.();
            }
        } catch {
            // Ignore display sync issues for continue.
        }
        if (keepReasoningPreview) showReasoningTokensExhaustedToast();
        return {
            response: combinedText,
            actionId: finalActionId,
            actionName: responseData.actionName,
            characterId: responseData.characterId,
            characterName: responseData.characterName,
            outfitId: finalOutfitId,
            isVideo: isVideoMsg
        };
    }

    const wasQueued = charResponse && conversationQueue && conversationQueue.queueResponse(responseData, charResponse, finalText);
    const isGreetingResponse = options.isRegularMessage === false;
    const sceneFields = await resolveSceneFieldsForMessage(responseData, hasNewLocation, {
        branchPath: normalizeMessagePath(responseData.path)
            || getActiveStreamSession()?.userPath
            || HangoutState.getInstance().getBranchTipPath?.()
    });

    if (wasQueued) {
        const addResult = await persistAssistantResponse({
            responseData,
            finalText: keepReasoningPreview ? '' : finalText,
            finalActionId,
            finalOutfitId,
            isVideoMsg,
            sceneFields,
            isGreetingResponse,
            shouldAdoptView,
            streamFindOptions,
            activeCharacters
        });
        if (charResponse.isQueued && addResult?.messagePath) {
            const queueIndex = conversationQueue.pendingResponses.findIndex(
                (item) => item.responseData.characterId === responseData.characterId
            );
            if (queueIndex !== -1) {
                conversationQueue.pendingResponses[queueIndex].messagePath = addResult.messagePath;
                conversationQueue.pendingResponses[queueIndex].keepReasoningPreview = keepReasoningPreview;
                if (keepReasoningPreview && charResponse.reasoning) {
                    conversationQueue.pendingResponses[queueIndex].streamingChunks = [charResponse.reasoning];
                }
            }
        }
        if (keepReasoningPreview) showReasoningTokensExhaustedToast();
        return;
    }

    if (charResponse && (charResponse.bubble || charResponse.characterId)) {
        const targetCharacterId = charResponse.characterId || responseData.characterId;
        const displayManager = await getDisplayManager();
        let targetMessage = displayManager.findMessageByCharacterId(targetCharacterId, streamFindOptions);
        const spriteStateForBubble = {
            characterId: targetCharacterId,
            actionId: finalActionId ?? null,
            actionName: responseData.actionName || null,
            outfitId: finalOutfitId ?? 0,
            isVideo: isVideoMsg
        };
        if (shouldAdoptView && !targetMessage && (finalText || keepReasoningPreview)) {
            targetMessage = await displayManager.createMessage(
                keepReasoningPreview ? (charResponse.reasoning || '') : finalText,
                responseData.characterName,
                targetCharacterId,
                spriteStateForBubble,
                streamFindOptions
            );
        }
        if (targetMessage) {
            targetMessage.spriteMeta = { ...(targetMessage.spriteMeta || {}), ...spriteStateForBubble };
            await displayManager.refreshMessageAvatar(targetCharacterId, spriteStateForBubble, streamFindOptions);
            if (keepReasoningPreview) {
                applyReasoningPreview(targetMessage, true);
            } else {
                applyReasoningPreview(targetMessage, false);
                displayManager.updateMessageText(targetCharacterId, finalText, streamFindOptions);
            }
        }
    }

    if (hasNewAction && shouldAdoptView) {
        try {
            const characterIndex = await getCharacterDisplayIndex(responseData.characterId);
            await patchCharacterInHangoutConfig(responseData.characterId, {
                actionId: responseData.actionId,
                ...(responseData.actionName ? { actionName: responseData.actionName } : {}),
                isVideo: isVideoMsg
            });
            await activateSpeakingUi(responseData.characterId, characterIndex, activeCharacters);
            await applySpeakingSprite(responseData, characterIndex, isVideoMsg, responseData.outfitId ?? finalOutfitId ?? 0);
        } catch (error) {
            console.error('message-stream: Error updating sprite in handleResponseEnd:', error);
        }
    }

    if (isRegularMessage && hasNewLocation && shouldAdoptView) {
        await handleLocationEvent(responseData, { requireValidLocation: true });
    }

    await persistAssistantResponse({
        responseData,
        finalText: keepReasoningPreview ? '' : finalText,
        finalActionId,
        finalOutfitId,
        isVideoMsg,
        sceneFields,
        isGreetingResponse,
        shouldAdoptView,
        streamFindOptions,
        activeCharacters
    });

    if (keepReasoningPreview) showReasoningTokensExhaustedToast();

    if (options.isRegularMessage === false) {
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('initialGreetingComplete', {
                detail: {
                    characterId: responseData.characterId,
                    characterName: responseData.characterName,
                    messageContent: finalText
                }
            }));
        }, 1000);
    }

    const isManualPacing = isVnLayoutMode() && storage.get('manualCharacterPacing') !== 'false';
    if (isManualPacing && !charResponse?.isQueued) {
        setTimeout(() => {
            if (conversationQueue?.pendingResponses?.length > 0) {
                conversationQueue.setWaitingState(true);
            }
        }, 100);
    }

    if (isRegularMessage && shouldAdoptView && responseData.characterId && hasNewOutfit) {
        await handleOutfitChange({ ...responseData, outfitId: finalOutfitId });
    }
    if (isRegularMessage && shouldAdoptView) {
        await updateHangoutConfigAfterMessage(responseData, hasNewOutfit ? finalOutfitId : null);
    }

    return {
        response: finalText,
        actionId: finalActionId,
        actionName: responseData.actionName,
        emotionId: responseData.emotionId,
        characterName: responseData.characterName,
        characterId: responseData.characterId,
        outfitId: finalOutfitId,
        location: hasNewLocation ? responseData.location : null,
        backgroundUrl: hasNewLocation ? responseData.backgroundUrl : null,
        isVideo: isVideoMsg
    };
};

const handleMessagePathsResolved = async (paths, options = {}) => {
    if (!Array.isArray(paths) || paths.length === 0) return;
    const hangoutState = HangoutState.getInstance();
    const messages = Array.isArray(hangoutState.getMessages()) ? [...hangoutState.getMessages()] : [];
    let didChange = false;
    const assistantBindings = [];
    const streamSession = getActiveStreamSession();
    const streamUserPath = normalizeMessagePath(options.streamUserPath || streamSession?.userPath);
    const streamGenerationId = options.streamGenerationId || streamSession?.id || null;
    const shouldAdoptView = !streamUserPath || isIncomingPathOnViewedBranch(streamUserPath);

    const matchesInFlightAssistant = (message, characterId, resolvedPath) => {
        if (message?.role !== 'assistant') return false;
        if (String(message.characterId ?? '') !== characterId) return false;
        if (normalizeMessagePath(message.path) === resolvedPath) return true;
        if (streamGenerationId && String(message.streamGenerationId || '') === String(streamGenerationId)) return true;
        if (streamUserPath && isDescendantPath(message.path, streamUserPath)) return true;
        return false;
    };

    const matchesInFlightUser = (message, resolvedPath) => {
        if (message?.role !== 'user') return false;
        if (normalizeMessagePath(message.path) === resolvedPath) return true;
        if (streamGenerationId && String(message.streamGenerationId || '') === String(streamGenerationId)) return true;
        if (streamUserPath && normalizeMessagePath(message.path) === streamUserPath) return true;
        return false;
    };

    for (const resolved of paths) {
        const resolvedPath = normalizeMessagePath(resolved?.path);
        if (!resolvedPath) continue;

        if (resolved.role === 'assistant') {
            const characterId = String(resolved.characterId ?? '');
            if (!characterId) continue;
            const canonicalPath = normalizeMessagePath(resolved.canonicalPath)
                || resolveCanonicalPath(resolvedPath, messages);
            assistantBindings.push({ characterId, path: resolvedPath, canonicalPath });
            for (let index = messages.length - 1; index >= 0; index -= 1) {
                const message = messages[index];
                if (!matchesInFlightAssistant(message, characterId, resolvedPath)) continue;
                if (normalizeMessagePath(message.path) === resolvedPath) break;
                messages[index] = { ...message, path: resolvedPath };
                if (shouldAdoptView) hangoutState.ensureActiveVersion(canonicalPath, resolvedPath);
                didChange = true;
                break;
            }
            continue;
        }

        if (resolved.role === 'user') {
            for (let index = messages.length - 1; index >= 0; index -= 1) {
                const message = messages[index];
                if (!matchesInFlightUser(message, resolvedPath)) continue;
                const pathChanged = normalizeMessagePath(message.path) !== resolvedPath;
                if (!pathChanged && message.persistedRemote === true) break;
                messages[index] = { ...message, path: resolvedPath, persistedRemote: true };
                didChange = true;
                break;
            }
        }
    }

    if (didChange) hangoutState.setMessages(messages);
    if (getCurrentLayoutMode() !== LAYOUT_MODES.CHATBOT) return;

    const displayManager = await getDisplayManager();
    if (shouldAdoptView && assistantBindings.length > 0) {
        for (const binding of assistantBindings) {
            displayManager.bindPathToLatestAssistantMessage?.(binding.characterId, binding.path, {
                canonicalPath: binding.canonicalPath || null,
                streamGenerationId
            });
        }
        return;
    }

    if (didChange && shouldAdoptView) {
        await displayManager.syncHistory?.(getTranscriptForLayout(hangoutState));
    }
};

const handleConversationIdResolved = async (conversationId) => {
    const normalizedConversationId = typeof conversationId === 'string' ? conversationId.trim() : '';
    if (!normalizedConversationId) return;
    if (!isResolvedConversationId(normalizedConversationId)) {
        console.warn('message-stream: Ignoring unresolved conversation_id_resolved payload', {
            conversationId: normalizedConversationId
        });
        return;
    }

    const hangoutState = HangoutState.getInstance();
    // Only conversation_id_resolved may persist conversationId into state/config.
    const configBeforeUpdate = await getHangoutConfig();
    const isAlreadyPersisted =
        hangoutState.getConversationId() === normalizedConversationId
        && configBeforeUpdate?.conversationId === normalizedConversationId;
    if (isAlreadyPersisted) return;

    await hangoutState.setConversationId(normalizedConversationId);

    let configAfterUpdate = await getHangoutConfig();
    if (configAfterUpdate?.conversationId !== normalizedConversationId) {
        await hangoutState.setConversationId(normalizedConversationId);
        configAfterUpdate = await getHangoutConfig();
    }

    if (configAfterUpdate?.conversationId !== normalizedConversationId) {
        await setHangoutConfig({
            ...configAfterUpdate,
            conversationId: normalizedConversationId
        });
    }

    try {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('conversationId', normalizedConversationId);
        window.history.replaceState({ path: currentUrl.toString() }, '', currentUrl.toString());
    } catch (error) {
        console.warn('message-stream: Failed to sync conversationId to URL:', error);
    }

    setChatMemory({ conversationId: normalizedConversationId });
    persistChatSummaryFromLocal(normalizedConversationId);
};

const OPENROUTER_KEY_STREAM_ERROR_CODES = new Set([
    'openrouter_key_invalid',
    'openrouter_key_credits',
    'openrouter_key_forbidden',
    'openrouter_key_rate_limited'
]);

const OPENROUTER_KEY_STREAM_ERROR_FALLBACKS = {
    openrouter_key_invalid: 'Your OpenRouter API key is invalid or expired. Update it in hangout settings.',
    openrouter_key_credits: 'Your OpenRouter account is out of credits. Add credits or clear the key in hangout settings.',
    openrouter_key_forbidden: 'OpenRouter rejected this request with your key. Check key permissions in hangout settings.',
    openrouter_key_rate_limited: 'OpenRouter rate-limited your key. Wait a moment and try again.'
};

const STREAM_ERROR_FALLBACKS = {
    ...OPENROUTER_KEY_STREAM_ERROR_FALLBACKS,
    response_generation_failed: "Couldn't generate a reply. Please try again.",
    rate_limit_exceeded: 'Free messages have been exhausted for today. Please select another response style.',
    scenario_load_failed: 'The scenario could not be loaded. Chat can continue without its description, goals, and greeting intents.'
};

const resolveStreamErrorMessage = (responseData) => {
    const code = responseData?.code;
    if (Object.prototype.hasOwnProperty.call(STREAM_ERROR_FALLBACKS, code)) {
        return getSettingsModalTranslationText(code, STREAM_ERROR_FALLBACKS[code]);
    }
    return responseData?.message;
};

export async function settleFailedTurnBubbles(characterId = null) {
    const displayManager = await getDisplayManager();
    const activeMessages = displayManager?.getActiveMessages?.() || [];
    const hangoutMessages = HangoutState.getInstance().getMessages?.() || [];
    const savedByPath = new Map();
    for (const entry of hangoutMessages) {
        const path = normalizeMessagePath(entry?.path);
        const content = String(entry?.content || '').trim();
        if (!path || !content) continue;
        savedByPath.set(path, entry.content);
    }

    for (const message of activeMessages) {
        if (!message || message.role === 'user' || message.role === 'system') continue;
        if (characterId != null && String(message.characterId) !== String(characterId)) continue;
        const pending = message.isTyping === true || message.isAwaitingDecision === true;
        if (!pending) continue;
        const savedText = savedByPath.get(normalizeMessagePath(message?.path)) || '';
        message.isTyping = false;
        message.isAwaitingDecision = false;
        applyReasoningPreview(message, false);
        message.setText?.(savedText);
        message.markDirty?.();
    }
    displayManager?.redrawAllMessages?.();
};

export async function processStreamingResponse(response, characters, _getBackgroundServiceInstance, options = {}) {
    const {
        isInitialGreeting = false,
        streamGenerationId = null,
        streamUserPath = null,
        isContinue = false,
        continuePath = null,
        continuePrefix = ''
    } = options;
    const streamOptions = {
        streamGenerationId: streamGenerationId || getActiveStreamSession()?.id || null,
        streamUserPath: streamUserPath || getActiveStreamSession()?.userPath || null,
        isContinue,
        continuePath,
        continuePrefix
    };
    const displayUsername = resolveUserNameFromStorage({ fallback: 'User' });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const characterResponses = new Map();
    const allStreamedResponses = [];
    let skipBilling = false;
    let energyApplied = false;
    const maybeApplyServerEnergy = (responseData) => {
        if (energyApplied) return;
        const buzz = Number(responseData?.buzz);
        if (!Number.isFinite(buzz)) return;
        energyApplied = true;
        applyEnergyFromServer({ count: buzz });
    };

    const offerSpriteCreation = (responseData) => {
        if (!getNsfwEnabled()) return;
        const offer = responseData?.spriteOffer;
        if (offer !== 'reference' && offer !== 'custom' && offer !== 'empty') return;
        const path = typeof responseData?.path === 'string' ? responseData.path.trim() : '';
        if ((offer === 'custom' || offer === 'empty') && !path) return;
        const idealActionId = Number(responseData?.idealActionId);
        if (offer === 'reference' && !(Number.isInteger(idealActionId) && idealActionId > 0)) return;
        const characterName = responseData.characterName || 'This character';
        const message = offer === 'reference'
            ? `We have a good image for this situation, but ${characterName} doesn't have it yet. Do you want to create it?`
            : offer === 'empty'
                ? 'It would seem we currently have no matching image for this query. Should we create one right now?'
                : "Looks like we don't have a good image for this situation yet. Do you want to create one?";
        void (async () => {
            const accepted = await showConfirm(message, { closable: true, yesText: 'Yes', noText: 'No' });
            if (!accepted) return;
            const responseOutfitId = Number(responseData.outfitId);
            const outfitId = hasValidResponseOutfitId(responseData.outfitId)
                ? responseOutfitId
                : Number(await getCurrentOutfitForCharacter(responseData.characterId)) || 0;
            if (offer === 'custom' || offer === 'empty') {
                await openMessageImagePromptModal({
                    path,
                    characterId: responseData.characterId,
                    outfitId,
                    source: 'chatbot-message-avatar'
                });
                return;
            }
            await openMessageSpriteReferenceModal({
                characterId: responseData.characterId,
                outfitId,
                actionId: idealActionId
            });
        })();
    };

    const streamHandlers = {
        stream_error: async (responseData) => {
            if (responseData?.code === 'scenario_load_failed') {
                try {
                    const { showWarning } = await import('../../utils/UnifiedNotificationSystem.js');
                    const message = resolveStreamErrorMessage(responseData);
                    if (message) showWarning(message, { duration: 8000 });
                } catch (notifErr) {
                    console.warn('message-stream: Failed to show scenario load warning:', notifErr);
                }
                return;
            }
            const characterId = responseData?.characterId;
            if (characterId != null) {
                characterResponses.delete(characterId);
            }
            try {
                await settleFailedTurnBubbles(characterId != null ? characterId : null);
            } catch (settleErr) {
                console.warn('message-stream: Failed to settle in-progress bubble:', settleErr);
            }
            try {
                const { showError } = await import('../../utils/UnifiedNotificationSystem.js');
                const message = resolveStreamErrorMessage(responseData);
                if (!message) return;
                const duration = OPENROUTER_KEY_STREAM_ERROR_CODES.has(responseData?.code) ? 8000 : 6000;
                showError(message, { duration });
            } catch (notifErr) {
                console.warn('message-stream: Failed to show stream_error notification:', notifErr);
            }
        },
        location_transition_start: (responseData) =>
            handleLocationEvent(responseData, { isInitialGreeting }),
        response_start: (responseData) =>
            handleResponseStart(responseData, characters, characterResponses, {
                isInitialGreeting,
                ...streamOptions
            }),
        content_chunk: (responseData) =>
            handleContentChunk(responseData, characterResponses, {
                displayUsername,
                activeCharacters: characters,
                ...streamOptions
            }),
        reasoning_chunk: (responseData) =>
            handleReasoningChunk(responseData, characterResponses, { displayUsername, ...streamOptions }),
        text_complete: async (responseData) => {
            await handleTextComplete(responseData, characterResponses, {
                activeCharacters: characters,
                displayUsername,
                ...streamOptions
            });
        },
        response_end: async (responseData) => {
            offerSpriteCreation(responseData);
            maybeApplyServerEnergy(responseData);
            const streamedResponse = await handleResponseEnd(responseData, characterResponses, {
                isRegularMessage: !isInitialGreeting,
                activeCharacters: characters,
                displayUsername,
                ...streamOptions
            });
            if (streamedResponse) allStreamedResponses.push(streamedResponse);
        },
        billing_skip: (responseData) => {
            skipBilling = responseData.skipBilling === true;
        },
        conversation_id_resolved: (responseData) =>
            handleConversationIdResolved(responseData.conversationId),
        message_paths_resolved: (responseData) =>
            handleMessagePathsResolved(responseData.paths, streamOptions),
        greeting_meta: async (responseData) => {
            HangoutState.getInstance().setGreetingVariationMeta({
                variation: responseData.greetingVariation,
                count: responseData.greetingVariationCount
            });
            try {
                const displayManager = await getDisplayManager();
                displayManager?.redrawAllMessages?.();
            } catch (redrawError) {
                console.warn('message-stream: greeting_meta redraw failed:', redrawError);
            }
        }
    };

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            let newlineIndex;
            while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
                const chunk = buffer.slice(0, newlineIndex).trim();
                buffer = buffer.slice(newlineIndex + 2);
                if (!chunk.startsWith('data: ')) continue;
                const jsonStr = chunk.slice(6);
                if (jsonStr === '[DONE]') break;

                try {
                    const responseData = JSON.parse(jsonStr);
                    const handler = streamHandlers[responseData.type];
                    if (handler) await handler(responseData);
                } catch (parseError) {
                    console.warn('message-stream: Failed to parse streaming chunk:', parseError, 'Raw chunk:', jsonStr);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
    return { responses: allStreamedResponses, skipBilling };
}
